import React, { useState } from 'react';
import './ResultDisplay.css';

const ResultDisplay = ({ result, onReset, isFallbackMode = false }) => {
  const [showFeatureMap, setShowFeatureMap] = useState(true);
  
  if (!result || !result.results) {
    return null;
  }
  
  // Use the result object for image info, no need for separate image prop
  const image = {
    url: result.url,
    width: result.width,
    height: result.height
  };
  
  const { score, starRating, keypoints, contrast, distribution, repetitiveness, recommendations, featureMap } = result.results;
  
  // More rigorous score category determination
  let scoreCategory;
  let scoreCategoryDescription;
  
  if (score >= 90) {
    scoreCategory = 'excellent';
    scoreCategoryDescription = 'Excellent - Should work very reliably';
  } else if (score >= 80) {
    scoreCategory = 'very-good';
    scoreCategoryDescription = 'Very Good - Should work in most conditions';
  } else if (score >= 70) {
    scoreCategory = 'good';
    scoreCategoryDescription = 'Good - Will work in good conditions';
  } else if (score >= 55) {
    scoreCategory = 'average';
    scoreCategoryDescription = 'Average - May work with limitations';
  } else if (score >= 40) {
    scoreCategory = 'poor';
    scoreCategoryDescription = 'Poor - Likely to have tracking issues';
  } else {
    scoreCategory = 'very-poor';
    scoreCategoryDescription = 'Very Poor - Not recommended for AR';
  }
  
  // Calculate more precise star rating based on score
  const calculatedStarRating = Math.min(5, Math.max(0, starRating));
  
  // Find where metric values are displayed and add number formatting
  
  // For the contrast value formatting, we need to add a helper function at the top of the component:
  const formatNumber = (value) => {
    // Ensure we're working with a number
    const num = parseFloat(value);
    if (isNaN(num)) return '0%';
    
    // Round to 1 decimal place
    return Math.round(num * 10) / 10;
  };
  
  // Generate stars display
  const renderStars = () => {
    // Round the rating to the nearest 0.5
    const roundedRating = Math.round(calculatedStarRating * 2) / 2;

    const fullStars = Math.floor(roundedRating); // Use rounded value
    const hasHalfStar = roundedRating % 1 !== 0; // Check if there's a .5
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <div className="star-rating">
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} className="star full-star">★</span>
        ))}
        {hasHalfStar && (
          <span className="star-wrapper">
            <span className="star half-star-bg">☆</span>
            <span className="star half-star-fill">★</span>
          </span>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} className="star empty-star">☆</span>
        ))}
      </div>
    );
  };
  
  // Determine if the dimensions are ideal for tracking
  const hasSizeConcerns = image.width < 300 || image.height < 300;
  const isSquarish = Math.max(image.width, image.height) / Math.min(image.width, image.height) < 1.5;
  
  return (
    <div className="result-display">
      {isFallbackMode && (
        <div className="fallback-result-notice">
          <p>⚠️ Basic Mode Analysis: These results are approximations since computer vision libraries couldn't be loaded.</p>
        </div>
      )}
      
      <div className="result-grid">
        <div className="result-image-container">
          <div className="image-toggle">
            {!isFallbackMode && featureMap && (
              <>
                <button 
                  className={`toggle-btn ${showFeatureMap ? '' : 'active'}`}
                  onClick={() => setShowFeatureMap(false)}
                >
                  Original
                </button>
                <button 
                  className={`toggle-btn ${showFeatureMap ? 'active' : ''}`}
                  onClick={() => setShowFeatureMap(true)}
                >
                  Feature Detection
                </button>
              </>
            )}
          </div>
          
          <div className="result-image">
            <img 
              src={isFallbackMode || !showFeatureMap || !featureMap ? image.url : featureMap} 
              alt={showFeatureMap && !isFallbackMode && featureMap ? "Image with detected features" : "Original image"} 
            />
            {showFeatureMap && !isFallbackMode && featureMap && (
              <div className="feature-map-legend">
                <span className="legend-dot"></span> 
                <span>Feature points ({keypoints.count}) detected on grayscale image</span>
              </div>
            )}
          </div>
          
          <div className="image-info">
            <div>Image dimensions: {image.width} × {image.height}px</div>
            {hasSizeConcerns ? (
              <div className="image-warning">
                ⚠️ This image is smaller than the recommended minimum size (300×300px)
              </div>
            ) : !isSquarish ? (
              <div className="image-warning">
                ⚠️ This image has a high aspect ratio. More square-like images typically work better.
              </div>
            ) : null}
          </div>
        </div>
        
        <div className="result-metrics">
          <div className={`score-display ${scoreCategory}`}>
            <div className="score-content">
              <div className="score-value">{score}</div>
              <div className="score-details">
                <div className="score-label">Trackability Score</div>
                <div className="score-category">{scoreCategoryDescription}</div>
                <div className="star-rating-container">
                  <div className="star-rating-value">{calculatedStarRating.toFixed(1)}</div>
                  {renderStars()}
                </div>
              </div>
            </div>
          </div>
          
          <div className="metrics-container">
            <div className="metric-item">
              <div className="metric-name">Features</div>
              <div className="metric-tooltip">Feature points create unique patterns that AR systems can identify</div>
              <div className="metric-bar">
                <div className="metric-fill" style={{ width: `${keypoints.score}%` }}></div>
              </div>
              <div className="metric-value">{typeof keypoints.count === 'number' ? `${keypoints.count} points` : keypoints.count} ({formatNumber(keypoints.score)}%)</div>
            </div>
            
            <div className="metric-item">
              <div className="metric-name">Contrast</div>
              <div className="metric-tooltip">High contrast between light and dark areas is essential for robust tracking</div>
              <div className="metric-bar">
                <div className="metric-fill" style={{ width: `${contrast.score}%` }}></div>
              </div>
              <div className="metric-value">{formatNumber(contrast.score)}%</div>
            </div>
            
            <div className="metric-item">
              <div className="metric-name">Distribution</div>
              <div className="metric-tooltip">Features should be well distributed across the entire image</div>
              <div className="metric-bar">
                <div className="metric-fill" style={{ width: `${distribution.score}%` }}></div>
              </div>
              <div className="metric-value">{typeof distribution.coverage === 'number' ? `${Math.round(distribution.coverage * 100)}% coverage` : 'N/A'} ({formatNumber(distribution.score)}%)</div>
            </div>
            
            <div className="metric-item">
              <div className="metric-name">Pattern Uniqueness</div>
              <div className="metric-tooltip">Repetitive patterns can confuse tracking algorithms</div>
              <div className="metric-bar">
                <div className="metric-fill" style={{ width: `${repetitiveness.score}%` }}></div>
              </div>
              <div className="metric-value">{formatNumber(repetitiveness.score)}%</div>
            </div>
          </div>
        </div>
      </div>
      
      {score < 90 && (
        <div className="result-footer">
          <div className="conclusion-section">
            <h3>Conclusion</h3>
            {recommendations && recommendations.length > 0 ? (
              <ul className="recommendations-list">
                {recommendations.map((rec, index) => {
                  // Remove any existing warning emoji from the beginning of the text
                  const cleanText = rec.replace(/^(⚠️|⚠)\s*/, '');
                  return (
                    <li key={index} className="recommendation-item">
                      <span className="recommendation-icon">⚠️</span> {cleanText}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="no-recommendations">
                This image has excellent tracking characteristics! No improvements needed.
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="action-container">
        <button className="button-primary analyze-btn" onClick={onReset}>
          Analyze Another Image
        </button>
      </div>
    </div>
  );
};

export default ResultDisplay; 